import {getAuth} from '../modules/auth/core/AuthHelpers'
import {API_URL} from './apiBase'

/**
 * Supplier `X-Tenant-Context` silent refresh (D6).
 *
 * hisbah-ui has no cookie of its own for the tenant-context token (that's the dumyah/customer web
 * flow) — the supplier portal is a pure SPA calling the Lumen API cross-origin, so it mints its own
 * short-lived context token via the session (`token` header) it already holds, and re-mints it before
 * the ≤5min TTL expires so the user never sees a break. `refreshTenantContextToken()` is invoked on
 * login success and on authenticated boot (see `Login.tsx` / `Auth.tsx`); the axios interceptor in
 * `AuthHelpers.ts` reads back whatever this module last stored.
 */

const ENDPOINT = `${API_URL}/api/partner-portal/tenant-context-token`
const SESSION_STORAGE_KEY = 'tt-tenant-context-token'
// Re-fetch this many ms before the token's own expiry — mirrors the customer-app client's skew.
const REFRESH_SKEW_MS = 60_000
// Bounded exponential backoff for a failed refresh (e.g. a transient 502) — the chain must never
// terminate permanently after one failure, since the interceptor has no other way to recover the
// header short of a full page reload.
const BASE_RETRY_DELAY_MS = 5_000
const MAX_RETRY_DELAY_MS = 5 * 60_000
// Floor for the scheduled refresh, so a missing/tiny `expires_in` can never spin a zero-delay loop.
const MIN_REFRESH_DELAY_MS = 30_000

type StoredContextToken = {token: string; exp: number}

type TenantContextTokenResponse = {token: string; token_type: string; expires_in: number}

let current: StoredContextToken | null = null
let refreshTimer: ReturnType<typeof setTimeout> | null = null
let retryAttempt = 0
// Bumped by clearTenantContextToken(): a mint still in flight when the user logs out must not
// persist the previous user's token (or restart the refresh chain) when it lands.
let generation = 0

function persist(entry: StoredContextToken): void {
  current = entry
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(entry))
  } catch {
    /* sessionStorage unavailable (private mode) — module state still serves the current tab */
  }
}

function clearScheduledTimer(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

/** Unref when available (Node/jsdom) so a pending refresh/retry timer never keeps a process alive. */
function unref(timer: ReturnType<typeof setTimeout>): void {
  ;(timer as unknown as {unref?: () => void}).unref?.()
}

function scheduleRefresh(expiresInSeconds: number): void {
  clearScheduledTimer()
  retryAttempt = 0
  const delayMs = Math.max(MIN_REFRESH_DELAY_MS, expiresInSeconds * 1000 - REFRESH_SKEW_MS || 0)
  refreshTimer = setTimeout(runScheduledRefresh, delayMs)
  unref(refreshTimer)
}

/**
 * On a failed refresh, retry with a bounded exponential backoff instead of dropping the refresh
 * chain forever — a single transient failure (e.g. one 502) must not leave the session running
 * with no X-Tenant-Context header for the rest of its lifetime.
 */
function scheduleRetry(): void {
  clearScheduledTimer()
  const delayMs = Math.min(BASE_RETRY_DELAY_MS * 2 ** retryAttempt, MAX_RETRY_DELAY_MS)
  retryAttempt += 1
  refreshTimer = setTimeout(runScheduledRefresh, delayMs)
  unref(refreshTimer)
}

function runScheduledRefresh(): void {
  refreshTenantContextToken().catch(() => {
    /* refreshTenantContextToken() already scheduled a bounded retry before rethrowing */
  })
}

/** The current, still-live token (module state first, then sessionStorage), or `null` if expired/absent. */
export function getStoredContextToken(): string | null {
  if (current && current.exp > Date.now()) return current.token
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as StoredContextToken
      if (parsed.exp > Date.now()) {
        current = parsed
        return parsed.token
      }
    }
  } catch {
    /* ignore — treat as absent */
  }
  return null
}

/**
 * Mint (or re-mint) the tenant-context token for the signed-in supplier user and schedule the next
 * refresh. A no-op when there's no session token yet (nothing to authenticate the mint request with).
 *
 * On failure, schedules a bounded-backoff retry before rethrowing, so the refresh chain always
 * self-heals from a transient error (callers may still swallow the rejection, e.g. `.catch(() =>
 * {})` at the login/boot call sites — that only affects THIS call, not the retry already queued).
 */
export async function refreshTenantContextToken(): Promise<void> {
  const auth = getAuth()
  // No session -> nothing to mint against, and deliberately NO retry scheduled: a signed-out tab
  // must not hold a live timer. The chain restarts from the explicit call in Login.tsx / Auth.tsx
  // the moment a session exists again.
  if (!auth?.token) return
  const startedGeneration = generation

  try {
    const res = await fetch(ENDPOINT, {
      method: 'GET',
      headers: {Accept: 'application/json', token: auth.token},
    })
    if (!res.ok) throw new Error(`tenant-context-token ${res.status}`)

    const body = (await res.json()) as TenantContextTokenResponse
    // Logged out (or a different session began) while the mint was in flight — drop the result.
    if (startedGeneration !== generation) return
    persist({token: body.token, exp: Date.now() + body.expires_in * 1000})
    scheduleRefresh(body.expires_in)
  } catch (err) {
    if (startedGeneration !== generation) return
    // Fail LOUDLY. Both call sites (`Login.tsx`, `Auth.tsx`) do `.catch(() => {})` and the scheduled
    // retry swallows too, so before this line a permanently-broken mint endpoint produced no signal
    // anywhere: the session simply ran forever with no `X-Tenant-Context` header. Log every failure
    // and keep retrying. The backoff runs 5s, 10s, 20s, 40s, 80s, 160s and then caps at 5 min,
    // so a broken endpoint logs ~6 times in the first five minutes and once per five minutes
    // after that — loud enough to notice, far short of a flood —
    // deliberately WITHOUT treating a 404/401 as terminal, because giving up quietly would hide
    // exactly the kind of missing-route outage this is most likely to be.
    // eslint-disable-next-line no-console
    console.error(
      `[tenant] tenant-context-token mint failed at ${ENDPOINT} — this session will send API ` +
        'requests with NO X-Tenant-Context header until a retry succeeds.',
      err
    )
    scheduleRetry()
    throw err
  }
}

/**
 * Clear all tenant-context state (module memory, sessionStorage, and any pending refresh/retry
 * timer). MUST be called on logout — otherwise a previous user's still-live token keeps being
 * returned by `getStoredContextToken()` (and stamped as `X-Tenant-Context`) until it naturally
 * expires, even after a different user signs in in the same tab.
 */
export function clearTenantContextToken(): void {
  generation += 1
  clearScheduledTimer()
  retryAttempt = 0
  current = null
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY)
  } catch {
    /* sessionStorage unavailable — nothing to clear */
  }
}
